// Content-Aware Fill using a simplified PatchMatch-inspired approach.
// Finds similar patches from surrounding non-damaged area and blends them
// into the masked region.

import { PixelBuffer } from "./PixelBuffer";

export interface ContentAwareFillOptions {
  samplingRadius: number; // max distance to search for source patches (default: 50)
  patchSize: number; // size of patch to compare (default: 5)
  colorAdaptation: number; // 0..1 how much to adapt source color to surroundings (default: 0.5)
  rotation: number; // degrees of random rotation to try (default: 0)
  scale: number; // scale factor to try (default: 1.0)
  mirror: boolean; // try mirrored patches (default: false)
}

const DEFAULT_OPTIONS: ContentAwareFillOptions = {
  samplingRadius: 50,
  patchSize: 5,
  colorAdaptation: 0.5,
  rotation: 0,
  scale: 1.0,
  mirror: false,
};

/**
 * Fill a masked region in a PixelBuffer using content-aware sampling.
 * The mask is a Uint8Array where 0 = keep, 255 = fill.
 * Returns a new PixelBuffer with the region filled.
 */
export function contentAwareFill(
  src: PixelBuffer,
  mask: Uint8Array,
  options: Partial<ContentAwareFillOptions> = {},
): PixelBuffer {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const w = src.width;
  const h = src.height;
  const result = new PixelBuffer(w, h, "rgba8");
  result.data.set(src.data);

  const patchSize = opts.patchSize;
  const halfPatch = Math.floor(patchSize / 2);
  const radius = opts.samplingRadius;

  // Collect all pixels that need filling
  const toFill: number[] = [];
  for (let i = 0; i < mask.length; i++) {
    if (mask[i]! > 128) toFill.push(i);
  }

  if (toFill.length === 0) return result;

  // Build a list of valid source pixels (non-masked)
  const validSrc: number[] = [];
  for (let i = 0; i < mask.length; i++) {
    if (mask[i]! <= 128) validSrc.push(i);
  }

  if (validSrc.length === 0) return result;

  // For each pixel to fill, find the best matching patch from valid sources
  // and copy the center pixel with color adaptation
  for (const fi of toFill) {
    const fy = Math.floor(fi / w);
    const fx = fi % w;

    // Find the best source pixel
    let bestSrcIdx = -1;
    let bestDist = Infinity;

    // Sample candidates within radius
    const numCandidates = Math.min(validSrc.length, 200);
    for (let c = 0; c < numCandidates; c++) {
      const si = validSrc[Math.floor(Math.random() * validSrc.length)]!;
      const sy = Math.floor(si / w);
      const sx = si % w;

      // Check distance constraint
      const dx = sx - fx;
      const dy = sy - fy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > radius) continue;

      // Compare patches
      const patchDist = comparePatches(result, mask, w, h, fx, fy, sx, sy, patchSize, halfPatch);
      if (patchDist < bestDist) {
        bestDist = patchDist;
        bestSrcIdx = si;
      }
    }

    // If no good source found within radius, use nearest valid pixel
    if (bestSrcIdx < 0) {
      let nearestDist = Infinity;
      for (const si of validSrc) {
        const sy = Math.floor(si / w);
        const sx = si % w;
        const d = Math.abs(sx - fx) + Math.abs(sy - fy);
        if (d < nearestDist) {
          nearestDist = d;
          bestSrcIdx = si;
        }
      }
    }

    if (bestSrcIdx >= 0) {
      const sy = Math.floor(bestSrcIdx / w);
      const sx = bestSrcIdx % w;
      const srcIdx = bestSrcIdx * 4;

      // Color adaptation: blend source color with average of surrounding filled pixels
      if (opts.colorAdaptation > 0) {
        let sumR = 0, sumG = 0, sumB = 0, count = 0;
        // Sample neighbors that are already filled
        for (let dy = -3; dy <= 3; dy++) {
          for (let dx = -3; dx <= 3; dx++) {
            const nx = fx + dx;
            const ny = fy + dy;
            if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
            const ni = (ny * w + nx) * 4;
            // Only use neighbors that were NOT in the mask (original pixels)
            const nm = ny * w + nx;
            if (mask[nm]! <= 128) {
              sumR += result.data[ni]!;
              sumG += result.data[ni + 1]!;
              sumB += result.data[ni + 2]!;
              count++;
            }
          }
        }

        const srcR = result.data[srcIdx]!;
        const srcG = result.data[srcIdx + 1]!;
        const srcB = result.data[srcIdx + 2]!;

        if (count > 0) {
          const avgR = sumR / count;
          const avgG = sumG / count;
          const avgB = sumB / count;
          const adapt = opts.colorAdaptation;
          result.data[fi * 4] = Math.round(srcR * (1 - adapt) + avgR * adapt);
          result.data[fi * 4 + 1] = Math.round(srcG * (1 - adapt) + avgG * adapt);
          result.data[fi * 4 + 2] = Math.round(srcB * (1 - adapt) + avgB * adapt);
          result.data[fi * 4 + 3] = result.data[srcIdx + 3]!;
        } else {
          result.data[fi * 4] = srcR;
          result.data[fi * 4 + 1] = srcG;
          result.data[fi * 4 + 2] = srcB;
          result.data[fi * 4 + 3] = result.data[srcIdx + 3]!;
        }
      } else {
        result.data[fi * 4] = result.data[srcIdx]!;
        result.data[fi * 4 + 1] = result.data[srcIdx + 1]!;
        result.data[fi * 4 + 2] = result.data[srcIdx + 2]!;
        result.data[fi * 4 + 3] = result.data[srcIdx + 3]!;
      }
    }
  }

  result.markDirty();
  return result;
}

/**
 * Compare two patches centered at (x1,y1) and (x2,y2).
 * Returns the sum of squared differences for non-masked pixels.
 */
function comparePatches(
  buf: PixelBuffer,
  mask: Uint8Array,
  w: number,
  h: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  patchSize: number,
  halfPatch: number,
): number {
  let dist = 0;
  let count = 0;

  for (let dy = -halfPatch; dy <= halfPatch; dy++) {
    for (let dx = -halfPatch; dx <= halfPatch; dx++) {
      const nx1 = x1 + dx;
      const ny1 = y1 + dy;
      const nx2 = x2 + dx;
      const ny2 = y2 + dy;

      if (nx1 < 0 || nx1 >= w || ny1 < 0 || ny1 >= h) continue;
      if (nx2 < 0 || nx2 >= w || ny2 < 0 || ny2 >= h) continue;

      // Only compare pixels where both are valid (non-masked)
      const m1 = ny1 * w + nx1;
      const m2 = ny2 * w + nx2;
      if (mask[m1]! > 128 && mask[m2]! > 128) continue;

      const i1 = (ny1 * w + nx1) * 4;
      const i2 = (ny2 * w + nx2) * 4;

      const dr = buf.data[i1]! - buf.data[i2]!;
      const dg = buf.data[i1 + 1]! - buf.data[i2 + 1]!;
      const db = buf.data[i1 + 2]! - buf.data[i2 + 2]!;

      dist += dr * dr + dg * dg + db * db;
      count++;
    }
  }

  return count > 0 ? dist / count : Infinity;
}
